import { type IETFLanguageTag } from '@opallabs/concepts'

export type CalendarPanelFormatters = Readonly<{
  /**
   * The day panel's heading: "Wednesday, July 15". No year — the month the
   * panel was opened from already names it, right beside the panel.
   */
  longDate: (date: Date) => string
  /** The day an item panel offers to go back to: "July 15, 2026". */
  mediumDate: (date: Date) => string
  /** A date inside a range or a breadcrumb: "Jul 15". */
  shortDate: (date: Date) => string
  /**
   * The short name of the zone an instant is read in: "PDT". The viewer's own
   * zone, because that is the zone every time in the calendar panel is already read in —
   * the month endpoint reports no zone of its own.
   *
   * `undefined` when the instant cannot be read or the platform names no zone.
   */
  timeZoneName: (isoInstant: string) => string | undefined
}>

/**
 * Built once by the calendar panel and handed to the day and item panels, so every date
 * reads the same way and no panel makes its own `Intl` formatter per render.
 */
export const buildPanelFormatters = (
  locale: IETFLanguageTag | undefined
): CalendarPanelFormatters => {
  const long = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
    weekday: 'long',
  })
  const medium = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
  const short = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
  })
  const zone = new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    timeZoneName: 'short',
  })

  return {
    longDate: (date) => long.format(date),
    mediumDate: (date) => medium.format(date),
    shortDate: (date) => short.format(date),
    timeZoneName: (isoInstant) => {
      const instant = new Date(isoInstant)
      if (Number.isNaN(instant.getTime())) return undefined

      return zone
        .formatToParts(instant)
        .find((part) => part.type === 'timeZoneName')?.value
    },
  }
}
